import { initSentry } from './lib/sentry';
initSentry();

import { StrictMode, useState, useCallback } from 'react';
import { createRoot } from 'react-dom/client';
import './index.css';
import App from './App';
import { I18nContext, getTranslations, type Lang } from './i18n';
import { initWhiteLabel } from './lib/whitelabel';
import { WhiteLabelProvider } from './lib/WhiteLabelContext';
import { TenantProvider } from './providers/TenantProvider';
import { OrganizacionProvider } from './providers/OrganizacionProvider';
import { AuthProvider } from './context/AuthContext';
import { ActiveDocumentProvider } from './context/ActiveDocumentContext';
import { ModuloAccesoProvider } from './hooks/useModuloAcceso';
// 0666b: React Query global para Action Center Premium + futuras vistas con cache.
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      refetchOnWindowFocus: true,
      retry: 1,
    },
  },
});

// Initialize white-label theme (tenant slug from URL or env)
const tenantSlug = new URLSearchParams(window.location.search).get('tenant') ?? undefined;
const wlConfig = initWhiteLabel(tenantSlug);

function getInitialLang(): Lang {
  const stored = localStorage.getItem('vianet-lang');
  if (stored === 'en' || stored === 'zh') return stored;
  return 'es';
}

function Root() {
  const [lang, setLangState] = useState<Lang>(getInitialLang);
  const setLang = useCallback((l: Lang) => {
    localStorage.setItem('vianet-lang', l);
    setLangState(l);
  }, []);
  const t = getTranslations(lang);

  return (
    <StrictMode>
      <TenantProvider>
        <OrganizacionProvider>
          <WhiteLabelProvider value={wlConfig}>
            <I18nContext.Provider value={{ lang, setLang, t }}>
              <AuthProvider>
                <QueryClientProvider client={queryClient}>
                  <ModuloAccesoProvider>
                    <ActiveDocumentProvider>
                      <App />
                    </ActiveDocumentProvider>
                  </ModuloAccesoProvider>
                </QueryClientProvider>
              </AuthProvider>
            </I18nContext.Provider>
          </WhiteLabelProvider>
        </OrganizacionProvider>
      </TenantProvider>
    </StrictMode>
  );
}

createRoot(document.getElementById('root')!).render(<Root />);
